import type { UserRole } from '../domain/user.types'
import { personName, pluralizeSk } from '../common/text'

export type EventType =
  | 'NABER'
  | 'OBHLIADKA'
  | 'ODOVZDAVKA'
  | 'PODPIS_RZ'
  | 'PODPIS_KZ'
  | 'PODPIS_ZOBKZ'
  | 'PODPIS_NZ'
  | 'PODPIS_VZ'
  | 'NOTAR'
  | 'BANKA'
  | 'SERVISNE_STRETNUTIE'
  | 'INE'
  | 'SUKROMNA'

export type EventStatus = 'SCHEDULED' | 'CONFIRMED' | 'DONE' | 'CANCELLED'

/**
 * Roly, ktoré si môžu zobraziť kalendár kolegu (admin dropdown makléra, mobilný
 * FAB kolegov, `GET /events/agent-options`). Server rozsah ďalej zužuje na
 * pobočku — viď apps/api/src/common/rbac/calendar-scope.ts.
 */
export const CALENDAR_VIEWER_ROLES: readonly UserRole[] = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER', 'ASSISTANT']

/** Roly, ktoré smú prideľovať úlohy iným („Komu je určená"). BROKER zadáva úlohy len sebe. */
export const TASK_ASSIGNER_ROLES: readonly UserRole[] = CALENDAR_VIEWER_ROLES

export function canViewColleagueCalendars(role: string | null | undefined): boolean {
  return CALENDAR_VIEWER_ROLES.includes(role as UserRole)
}

export function canAssignTasks(role: string | null | undefined): boolean {
  return TASK_ASSIGNER_ROLES.includes(role as UserRole)
}

/** Položka `GET /events/agent-options` — `locked` = owner, ktorý do mojej pobočky nezdieľa kalendár. */
export interface CalendarAgentOption {
  id: string
  firstName: string
  lastName: string
  role: string
  branchId: string | null
  locked: boolean
}

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  NABER: 'Náber',
  OBHLIADKA: 'Obhliadka',
  ODOVZDAVKA: 'Odovzdávka',
  PODPIS_RZ: 'Podpis RZ',
  PODPIS_KZ: 'Podpis KZ',
  PODPIS_ZOBKZ: 'Podpis ZoBKZ',
  PODPIS_NZ: 'Podpis NZ',
  PODPIS_VZ: 'Podpis VZ',
  NOTAR: 'Notár',
  BANKA: 'Banka',
  SERVISNE_STRETNUTIE: 'Servisné stretnutie',
  INE: 'Stretnutie',
  SUKROMNA: 'Súkromná',
}

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  SCHEDULED: 'Naplánovaná',
  CONFIRMED: 'Potvrdená',
  DONE: 'Prebehla',
  CANCELLED: 'Zrušená',
}

/** Poradie stavov v dropdownoch (admin) a v enum schéme AI asistenta. */
export const EVENT_STATUSES: EventStatus[] = ['SCHEDULED', 'CONFIRMED', 'DONE', 'CANCELLED']

/** Poradie v dropdownoch (mobil aj admin) — „Stretnutie" (INE) prvé; default typu je stále OBHLIADKA. */
export const EVENT_TYPES: EventType[] = [
  'INE', 'NABER', 'OBHLIADKA', 'ODOVZDAVKA',
  'PODPIS_RZ', 'PODPIS_KZ', 'PODPIS_ZOBKZ', 'PODPIS_NZ', 'PODPIS_VZ',
  'NOTAR', 'BANKA', 'SERVISNE_STRETNUTIE', 'SUKROMNA',
]

/**
 * Typy, pri ktorých má `customTitle` význam: INE = vlastný názov udalosti,
 * NABER = názov naberanej nehnuteľnosti (ešte nie je v systéme, takže sa
 * namiesto väzby na nehnuteľnosť zapíše voľný text). Ostatné typy ho nemajú —
 * server ho pri nich zahadzuje.
 * SUKROMNA = voliteľný názov, ktorý vidí len majiteľ (ostatným API vracia redigovaný objekt).
 */
export function eventTypeHasCustomTitle(type: EventType | string): boolean {
  return type === 'INE' || type === 'NABER' || type === 'SUKROMNA'
}

/**
 * Jediný zdroj pravdy pre názov udalosti: pri type INE vlastný názov,
 * inak label typu. Fallback na EVENT_TYPE_LABELS aj pre INE bez customTitle
 * (historické/nevalidné dáta nesmú vyrenderovať prázdny titulok).
 */
export function getEventTitle(e: { type: EventType | string; customTitle?: string | null }): string {
  if ((e.type === 'INE' || e.type === 'SUKROMNA') && e.customTitle?.trim()) return e.customTitle.trim()
  return EVENT_TYPE_LABELS[e.type as EventType] ?? String(e.type)
}

/**
 * „Čo/kde" udalosti namiesto väzby na nehnuteľnosť: pri NÁBERE názov
 * naberanej nehnuteľnosti z customTitle, inak názov prepojenej nehnuteľnosti
 * (fallback obec). Jediný zdroj pravdy pre mobil, admin aj API (gcal, notifikácie).
 */
export function getEventPropertyLabel(e: {
  type: EventType | string
  customTitle?: string | null
  property?: { title?: string | null; city?: string | null } | null
}): string | null {
  if (e.type === 'NABER' && e.customTitle?.trim()) return e.customTitle.trim()
  return e.property?.title?.trim() || e.property?.city?.trim() || null
}

/** Label účastníka udalosti: osoba (kontakt) > voľný telefónny kontakt. */
export function getEventPartyLabel(e: {
  contact?: { firstName?: string | null; lastName?: string | null } | null
  contactName?: string | null
}): string | null {
  return personName(e.contact) || (e.contactName ?? null)
}

/** „1 obhliadka prebehla" / „2 obhliadky prebehli" / „5 obhliadok prebehlo" — karta Udalosti v admine aj mobile. */
export function viewingsDoneLabel(n: number): { count: string; verb: string } {
  return {
    count: `${n} ${pluralizeSk(n, ['obhliadka', 'obhliadky', 'obhliadok'])}`,
    verb: pluralizeSk(n, ['prebehla', 'prebehli', 'prebehlo']),
  }
}

/**
 * Doména ICS UID, ktoré generujeme my. Odlišuje naše vlastné pozvánky od
 * externých (Google, Outlook) — na tom závisí, či sa odpoveď zapíše na
 * existujúcu udalosť, alebo sa vyrobí nová z cudzej pozvánky. Konštanta,
 * nie odvodenina z CALENDAR_EMAIL: UID je trvalý identifikátor, zmena
 * odosielacej schránky nesmie osirieť už rozposlané pozvánky.
 */
export const INTERNAL_ICS_UID_DOMAIN = 'realnexa.sk'

/** ICS UID našej udalosti — jediné miesto, kde tento tvar vzniká. */
export function buildInternalIcsUid(eventId: string): string {
  return `${eventId}@${INTERNAL_ICS_UID_DOMAIN}`
}

const INTERNAL_UID_RE = new RegExp(
  `^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})@${INTERNAL_ICS_UID_DOMAIN.replace(/\./g, '\\.')}$`,
  'i',
)

/**
 * ID udalosti z nášho ICS UID, alebo null pre cudzie UID. Volajúci MUSÍ overiť,
 * že udalosť naozaj existuje v jeho tenantovi — samotný tvar UID je verejný a
 * externý odosielateľ ho vie napodobniť.
 */
export function parseInternalIcsUid(uid: string): string | null {
  return INTERNAL_UID_RE.exec(uid.trim())?.[1]?.toLowerCase() ?? null
}

/**
 * Predvoľby pripomienok pred udalosťou (minúty) — multi-select per event,
 * default [60] (1 hodinu pred). Jediný zdroj pravdy pre mobil, admin aj API.
 */
export const EVENT_REMINDER_PRESETS = [5, 10, 15, 30, 60, 120, 1440] as const

/** Uložený tvar pripomienok: bez duplicít, vzostupne — jediný normalizátor pre Event aj Task. */
export function normalizeReminderMinutes(minutes: number[]): number[] {
  return [...new Set(minutes)].sort((a, b) => a - b)
}

/** „15 minút pred", „1 hodinu pred", „1 deň pred" — pre picker aj notifikácie. */
export function formatReminderOffset(minutes: number): string {
  if (minutes < 60) {
    return minutes === 1 ? '1 minútu pred' : minutes < 5 ? `${minutes} minúty pred` : `${minutes} minút pred`
  }
  if (minutes < 1440) {
    const h = Math.round(minutes / 60)
    return h === 1 ? '1 hodinu pred' : h < 5 ? `${h} hodiny pred` : `${h} hodín pred`
  }
  const d = Math.round(minutes / 1440)
  return d === 1 ? '1 deň pred' : d < 5 ? `${d} dni pred` : `${d} dní pred`
}

/** Krátky tvar do subjektu notifikácie: „o 15 min", „o 1 h", „zajtra". */
export function formatReminderSubjectSuffix(minutes: number): string {
  if (minutes < 60) return `o ${minutes} min`
  if (minutes < 1440) return `o ${Math.round(minutes / 60)} h`
  return minutes === 1440 ? 'zajtra' : `o ${Math.round(minutes / 1440)} d`
}

/**
 * Scheduler beží v 5-minútovom cykle, takže pripomienka odíde až do 5 minút po
 * svojom nominálnom čase. Drift v rámci tohto okna je vlastnosť cronu, nie
 * omeškanie — label si vtedy ponecháva nominálny offset.
 */
const REMINDER_CRON_GRANULARITY_MIN = 5

/**
 * Koľko minút ukázať v subjekte pripomienky.
 *
 * Scheduler dobieha zmeškané offsety (udalosť vytvorená alebo presunutá až po
 * tom, čo nominálny čas pripomienky prešiel), takže samotný offset vie klamať —
 * udalosť založená 18:39 na 19:00 s 30-minútovou pripomienkou hlásila „o 30 min",
 * hoci zostávalo 20. Nominál preto platí len v rámci granularity cronu; čokoľvek
 * neskoršie hlási reálny zostávajúci čas.
 */
export function resolveReminderDisplayMinutes(offsetMin: number, remainingMin: number): number {
  if (offsetMin - remainingMin <= REMINDER_CRON_GRANULARITY_MIN) return offsetMin
  // Pripomienka nikdy nesmie tvrdiť „o 0 min" — v tej chvíli udalosť ešte nezačala.
  return Math.max(1, remainingMin)
}

/**
 * Farby udalostí — pomenované kľúče, nie hex: mobil ich mapuje na svoju
 * svetlú/tmavú paletu (rovnomenné kľúče Palette), admin na EVENT_COLOR_HEX.
 * Udalosť bez farby (null, importy z gcal/ICS) = 'blue' (doterajšia farba).
 */
export const EVENT_COLORS = [
  'blue', 'green', 'amber', 'red', 'purple', 'teal', 'orange',
  'pink', 'indigo', 'lime', 'cyan', 'gray', 'brown',
] as const
export type EventColor = (typeof EVENT_COLORS)[number]

export const DEFAULT_EVENT_COLOR: EventColor = 'blue'

export const EVENT_COLOR_HEX: Record<EventColor, string> = {
  blue: '#3B82F6', green: '#22C55E', amber: '#F59E0B', red: '#EF4444',
  purple: '#8B5CF6', teal: '#14B8A6', orange: '#F97316',
  pink: '#EC4899', indigo: '#6366F1', lime: '#84CC16', cyan: '#06B6D4', gray: '#6B7280',
  brown: '#92400E',
}

export const EVENT_COLOR_LABELS: Record<EventColor, string> = {
  blue: 'Modrá', green: 'Zelená', amber: 'Žltá', red: 'Červená',
  purple: 'Fialová', teal: 'Tyrkysová', orange: 'Oranžová',
  pink: 'Ružová', indigo: 'Indigová', lime: 'Limetková', cyan: 'Azúrová', gray: 'Sivá',
  brown: 'Hnedá',
}

/**
 * Vstavaná predvolená farba každého typu udalosti — 13 typov, 13 rôznych
 * farieb. Mobil ju používa pri tvorbe udalosti (používateľ si ju vie per
 * zariadenie prepísať v Nastavenia → Kalendár → Farby udalostí) a pri
 * vykreslení udalosti bez farby (null = import z gcal/ICS). Ručne zvolená
 * farba udalosti má vždy prednosť — toto je len default.
 */
export const DEFAULT_EVENT_TYPE_COLORS: Record<EventType, EventColor> = {
  INE: 'blue',
  NABER: 'orange',
  OBHLIADKA: 'green',
  ODOVZDAVKA: 'teal',
  PODPIS_RZ: 'purple',
  PODPIS_KZ: 'red',
  PODPIS_ZOBKZ: 'pink',
  PODPIS_NZ: 'indigo',
  PODPIS_VZ: 'cyan',
  NOTAR: 'amber',
  BANKA: 'lime',
  SERVISNE_STRETNUTIE: 'brown',
  SUKROMNA: 'gray',
}

/** Predvolená farba typu; neznámy typ (historické dáta) → DEFAULT_EVENT_COLOR. */
export function defaultEventTypeColor(type: EventType | string): EventColor {
  return DEFAULT_EVENT_TYPE_COLORS[type as EventType] ?? DEFAULT_EVENT_COLOR
}

/**
 * Greedy priradenie „dráh" viacdňovým pásom v mesačnej mriežke — prekrývajúce
 * sa pásy dostanú rôzne dráhy a pás si tú svoju drží po celé trvanie (aj cez
 * hranice týždňov). Kľúče dní sú lexikograficky porovnateľné (YYYY-MM-DD).
 */
export function assignSpanLanes(
  spans: { id: string; startKey: string; endKey: string }[],
): Record<string, number> {
  const sorted = [...spans].sort(
    (a, b) => a.startKey.localeCompare(b.startKey) || b.endKey.localeCompare(a.endKey),
  )
  const laneEnds: string[] = []
  const lanes: Record<string, number> = {}
  for (const s of sorted) {
    let lane = laneEnds.findIndex((end) => end < s.startKey)
    if (lane === -1) {
      lane = laneEnds.length
      laneEnds.push(s.endKey)
    } else {
      laneEnds[lane] = s.endKey
    }
    lanes[s.id] = lane
  }
  return lanes
}

export const ATTENDEE_STATUSES = ['NEEDS_ACTION', 'ACCEPTED', 'DECLINED', 'TENTATIVE'] as const
export type AttendeeStatusValue = (typeof ATTENDEE_STATUSES)[number]

export const ATTENDEE_STATUS_LABELS: Record<AttendeeStatusValue, string> = {
  NEEDS_ACTION: 'Čaká', ACCEPTED: 'Prijal', DECLINED: 'Odmietol', TENTATIVE: 'Možno',
}

/** Admin `.badge-*` class per status. */
export const ATTENDEE_STATUS_BADGE_CLASS: Record<AttendeeStatusValue, string> = {
  NEEDS_ACTION: 'badge-gray', ACCEPTED: 'badge-green', DECLINED: 'badge-red', TENTATIVE: 'badge-yellow',
}

// ── Pozvánky v kalendári ───────────────────────────────────────────────────────

export type CalendarInviteSource = 'INTERNAL' | 'EXTERNAL' | 'GCAL'

/**
 * Stav pozvánky vlastníka zobrazeného kalendára na položke `GET /events`.
 * `null`/chýba = vlastná udalosť. DECLINED sa do kalendára nevracia vôbec.
 * INTERNAL = kolega pridal vlastníka ako účastníka (id = EventAttendee.id);
 * EXTERNAL = ICS z e-mailu, GCAL = neprijatá pozvánka z Google syncu
 * (id = EmailCalendarInvite.id) — tie sú virtuálne položky bez detailu.
 */
export interface CalendarInvite {
  status: Exclude<AttendeeStatusValue, 'DECLINED'>
  source: CalendarInviteSource
  id: string
  emailMessageId: string | null
}

/** Prefix `id` virtuálnej položky kalendára (externá neprijatá pozvánka). */
export const VIRTUAL_INVITE_PREFIX = 'invite:'

/** Šrafovať: pozvánka vlastníka kalendára, na ktorú ešte neodpovedal. */
export function isPendingInvite(e: { invite?: CalendarInvite | null }): boolean {
  return e.invite?.status === 'NEEDS_ACTION'
}

/** Virtuálna položka (externá pozvánka) — nemá detail, otvára Pozvánky / e-mail. */
export function isVirtualInvite(e: { invite?: CalendarInvite | null }): boolean {
  return !!e.invite && e.invite.source !== 'INTERNAL'
}

// ── Kolízia termínu ────────────────────────────────────────────────────────────

/**
 * Okno `GET /events?from&to` pre kontrolu kolízie. API filtruje len podľa
 * začiatku, preto siaha 7 dní dozadu (dlhšia udalosť, ktorá začala skôr, do
 * termínu zasahuje). Celé dni → kľúč dopytu sa pri zmene času v rámci dňa nemení.
 */
export function eventConflictRange(start: Date, end: Date): { from: string; to: string } {
  const from = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 7)
  const to = new Date(end.getFullYear(), end.getMonth(), end.getDate() + 1)
  return { from: from.toISOString(), to: to.toISOString() }
}

/**
 * Udalosti, s ktorými sa termín [start, end) prekrýva — upozornenie vo formulári
 * novej udalosti. Nerátajú sa zrušené ani celodenné (dovolenka, termín — nie
 * obsadený čas); dotyk (koniec jednej = začiatok druhej) kolízia nie je.
 */
export function findEventConflicts<T extends {
  scheduledAt: string | Date
  duration?: number | null
  allDay?: boolean | null
  status: EventStatus | string
}>(events: readonly T[], start: Date, end: Date): T[] {
  const s = start.getTime()
  const e = end.getTime()
  if (!(e > s)) return []
  return events.filter((ev) => {
    if (ev.allDay || ev.status === 'CANCELLED') return false
    const evStart = new Date(ev.scheduledAt).getTime()
    const evEnd = evStart + (ev.duration ?? 60) * 60_000
    return evStart < e && s < evEnd
  })
}

// ── História udalostí pri klientovi / kontakte / nehnuteľnosti ─────────────────

/**
 * Výsledok udalosti z pohľadu účasti: DONE = prebehla (status DONE, alebo
 * SCHEDULED/CONFIRMED s koncom v minulosti — udalosti sa na DONE neprepínajú
 * automaticky), UPCOMING = ešte len bude, CANCELLED = neúčasť.
 */
export type EventOutcome = 'DONE' | 'UPCOMING' | 'CANCELLED'

export const EVENT_OUTCOME_LABELS: Record<EventOutcome, string> = {
  DONE: 'Prebehla',
  UPCOMING: 'Naplánovaná',
  CANCELLED: 'Zrušená',
}

/** Admin `.badge-*` class per outcome. */
export const EVENT_OUTCOME_BADGE_CLASS: Record<EventOutcome, string> = {
  DONE: 'badge-green', UPCOMING: 'badge-blue', CANCELLED: 'badge-gray',
}

export function resolveEventOutcome(e: {
  status: EventStatus | string
  scheduledAt: string | Date
  duration: number
}, now: Date = new Date()): EventOutcome {
  if (e.status === 'DONE') return 'DONE'
  if (e.status === 'CANCELLED') return 'CANCELLED'
  const end = new Date(e.scheduledAt).getTime() + (e.duration || 0) * 60_000
  return end < now.getTime() ? 'DONE' : 'UPCOMING'
}

/** Riadok z `GET /events/for-entity` — udalosť naviazaná na osobu (kontakt) alebo nehnuteľnosť. */
export interface EntityEventItem {
  id: string
  type: EventType
  customTitle: string | null
  status: EventStatus
  scheduledAt: string
  duration: number
  allDay: boolean
  /** Séria opakovanej udalosti (null = jednorazová). */
  seriesId?: string | null
  outcome: EventOutcome
  /** Či ju môže prihlásený používateľ otvoriť (vlastná / pozvaný / manažér). Cudzie sa len zobrazujú. */
  canOpen: boolean
  feedback: string | null
  rating: number | null
  agent: { id: string; firstName: string; lastName: string } | null
  /** Priama väzba alebo nehnuteľnosť obchodu. */
  property: { id: number; title: string; city: string | null } | null
  /** Osoba udalosti (kontakt). */
  contact: { id: string; firstName: string; lastName: string } | null
  deal: { id: number; title: string } | null
}

export interface EntityEventsSummary {
  /** Obhliadky, ktoré prebehli. */
  viewingsDone: number
  /** Všetky udalosti (vrátane obhliadok), ktoré prebehli. */
  eventsDone: number
  /** Naplánované (budúce) udalosti. */
  upcoming: number
}

export interface EntityEventsResponse {
  summary: EntityEventsSummary
  events: EntityEventItem[]
}
